import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, LOGO_URL } from "@/lib/api";
import { money0, sessionId, trackPartner, PARTNER_IMG } from "@/lib/partners";
import { ActivationPanel } from "@/components/partner/ActivationPanel";
import { ArrowRight, ArrowDown, Check, Phone } from "lucide-react";

const Kicker = ({ children }) => (
  <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.32em] text-[#c19a6b] font-bold">{children}</p>
);

const Stage = ({ img, title, body, index }) => (
  <div className="flex-1 fade-up" style={{ animationDelay: `${index * 120}ms` }}>
    <div className="overflow-hidden">
      <img src={img} alt={title} className="w-full aspect-[4/3] object-cover hover:scale-[1.03] transition-transform duration-700" loading="lazy" />
    </div>
    <p className="text-[10px] uppercase tracking-[0.28em] text-[#c19a6b] mt-6">{title}</p>
    <p className="text-[15px] text-gray-600 leading-relaxed mt-3">{body}</p>
  </div>
);

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

export default function PartnerPresentation() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/public/partners/${slug}`, { params: { sid: sessionId() } })
      .then((r) => setD(r.data))
      .catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 text-center">
        <img src={LOGO_URL} alt="ARK Furniture" className="h-12 mb-8" />
        <h1 className="font-display text-2xl mb-3">This partnership page isn't available</h1>
        <p className="text-gray-500 text-sm">Please contact ARK Furniture for an updated link.</p>
      </div>
    );
  }

  if (!d) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-black border-t-[#c19a6b] rounded-full animate-spin" />
      </div>
    );
  }

  const r = d.realtor;
  const first = r.first_name || r.display_name;
  const initials = `${(r.first_name || "")[0] || ""}${(r.last_name || "")[0] || ""}`.toUpperCase();
  const avgSavings = d.examples.length
    ? Math.round(d.examples.reduce((a, e) => a + e.savings_cents, 0) / d.examples.length)
    : 0;
  const topSavings = d.examples.reduce((a, e) => Math.max(a, e.savings_cents), 0);

  const valueStack = [
    "Personalized client landing page",
    "Unique client link",
    "Personalized QR code",
    "Signature Revival at Essential pricing for your clients",
    "Digital client card",
    "Printable client card",
    "Client-facing marketing assets",
    "Old-home pickup / new-home delivery option",
    "Client tracking and reporting",
    "ARK handles all customer communication",
    "No contracts",
    "No minimum referrals",
    "No cost",
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Slim top bar */}
      <header className="bg-[#0B0B0B] text-white sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="font-display text-sm tracking-[0.35em]">ARK</span>
            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.24em] text-white/40">{d.program_name}</span>
          </div>
          <button
            data-testid="topbar-activate-btn"
            onClick={() => { trackPartner(slug, "activation_click", { from: "topbar" }); scrollTo("activate"); }}
            className="text-[10px] uppercase tracking-[0.2em] border border-[#c19a6b] text-[#c19a6b] hover:bg-[#c19a6b] hover:text-white px-4 py-2 transition-colors duration-200"
          >
            {d.activated ? "My Client Materials" : "Activate"}
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="bg-[#0B0B0B] text-white overflow-hidden">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 sm:py-28 lg:py-36 grid lg:grid-cols-[1.15fr_1fr] gap-16 lg:gap-20 items-center">
          <div className="fade-up">
            <div className="flex items-center gap-4 mb-8">
              {r.headshot_url ? (
                <img src={r.headshot_url} alt={r.display_name} className="w-14 h-14 rounded-full object-cover ring-1 ring-[#c19a6b]/60" />
              ) : (
                <div className="w-14 h-14 rounded-full border border-[#c19a6b]/50 flex items-center justify-center">
                  <span className="font-display text-lg text-[#c19a6b]">{initials || "ARK"}</span>
                </div>
              )}
              <div>
                <p className="text-[10px] uppercase tracking-[0.28em] text-[#c19a6b]">Prepared for</p>
                <p className="text-sm text-white/85 mt-1">
                  {r.display_name}
                  {r.brokerage && <span className="text-white/40"> · {r.brokerage}</span>}
                </p>
              </div>
            </div>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight" data-testid="presentation-headline">
              {first.toUpperCase()}, WE BUILT SOMETHING FOR YOUR CLIENTS.
            </h1>
            <p className="text-lg sm:text-xl text-white/60 mt-8 leading-relaxed max-w-xl">
              Give your clients a premium moving benefit — at no cost to you.
            </p>
            <p className="text-[15px] text-white/40 mt-4 leading-relaxed max-w-xl">
              We've created a private ARK Furniture benefit exclusively for clients of {r.display_name}.
            </p>

            <div className="border-l-2 border-[#c19a6b] pl-6 mt-12">
              <p className="text-[10px] uppercase tracking-[0.28em] text-white/40">Your clients receive</p>
              <p className="font-display text-3xl sm:text-4xl mt-3 leading-tight">
                Signature Revival
                <span className="block text-[#c19a6b] text-2xl sm:text-3xl mt-1.5">at the Essential Revival rate</span>
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mt-14">
              <button
                data-testid="hero-see-clients-btn"
                onClick={() => scrollTo("client-experience")}
                className="h-14 px-9 bg-white text-black text-[11px] uppercase tracking-[0.22em] font-bold btn-lift inline-flex items-center justify-center gap-2"
              >
                See What Your Clients Receive <ArrowRight size={15} />
              </button>
              <button
                data-testid="hero-activate-btn"
                onClick={() => { trackPartner(slug, "activation_click", { from: "hero" }); scrollTo("activate"); }}
                className="h-14 px-9 border border-white/25 hover:border-[#c19a6b] text-white text-[11px] uppercase tracking-[0.22em] transition-colors duration-200 inline-flex items-center justify-center"
              >
                Activate My Client Benefit
              </button>
            </div>
          </div>

          <div className="relative fade-up" style={{ animationDelay: "150ms" }}>
            <img src={PARTNER_IMG.hero} alt="Revived furniture in a styled dining room" className="w-full aspect-[4/5] object-cover" />
            <div className="absolute -bottom-6 -left-6 sm:-left-10 bg-[#c19a6b] text-white px-6 py-5 max-w-[230px]">
              <p className="text-[10px] uppercase tracking-[0.22em] text-white/70">Client saves up to</p>
              <p className="font-display text-3xl mt-1" data-testid="hero-savings">{money0(topSavings)}</p>
              <p className="text-[11px] text-white/70 mt-1 leading-snug">per piece, versus our published Signature rate</p>
            </div>
          </div>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <Kicker>The moving reality</Kicker>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-5 max-w-2xl leading-tight">
          Moving is already complicated enough.
        </h2>
        <p className="text-gray-500 mt-6 max-w-xl leading-relaxed">Somewhere between listing and closing, your clients are deciding:</p>
        <div className="grid sm:grid-cols-2 gap-x-16 mt-12">
          {[
            "What furniture should come with us?",
            "What should be replaced?",
            "Will anything get damaged during the move?",
            "Will our existing pieces work in the new home?",
            "Where will everything go?",
            "Should we store some of it temporarily?",
          ].map((q, i) => (
            <p key={q} className="text-[17px] font-display border-b border-gray-100 py-5 fade-up" style={{ animationDelay: `${i * 60}ms` }}>
              {q}
            </p>
          ))}
        </div>
        <p className="font-display text-2xl sm:text-3xl text-[#c19a6b] mt-16 max-w-lg leading-snug">
          ARK removes one of those headaches entirely.
        </p>
      </section>

      {/* OLD HOME -> ARK -> NEW HOME */}
      <section className="bg-[#FAF7F2] py-24 sm:py-32">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl">
            <Kicker>The handoff</Kicker>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-5 leading-tight">
              The furniture they love.
              <span className="block text-[#c19a6b]">Ready for the home they're going to love.</span>
            </h2>
          </div>
          <div className="flex flex-col lg:flex-row items-stretch gap-10 lg:gap-6 mt-16">
            <Stage index={0} img={PARTNER_IMG.oldHome} title="Old Home" body="We pick up the furniture before or during the move." />
            <div className="flex items-center justify-center lg:pt-24">
              <ArrowRight className="text-[#c19a6b] rotate-90 lg:rotate-0" size={24} />
            </div>
            <Stage index={1} img={PARTNER_IMG.workshop} title="ARK Workshop" body="We professionally revive the pieces while your client moves and settles in." />
            <div className="flex items-center justify-center lg:pt-24">
              <ArrowRight className="text-[#c19a6b] rotate-90 lg:rotate-0" size={24} />
            </div>
            <Stage index={2} img={PARTNER_IMG.newHome} title="New Home" body="We deliver the refreshed furniture into their new home." />
          </div>
        </div>
      </section>

      {/* EXCLUSIVE PRICING */}
      <section className="bg-[#0B0B0B] text-white py-24 sm:py-32" data-testid="exclusive-pricing">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <Kicker>Exclusive client pricing</Kicker>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-5 leading-tight max-w-2xl">
            The Signature experience, at the Essential rate.
          </h2>
          <p className="text-white/45 mt-6 max-w-xl leading-relaxed">
            Pricing pulled live from ARK's master price list — it stays current automatically.
          </p>

          <div className="mt-14 border-t border-white/10">
            <div className="hidden sm:grid grid-cols-[1.4fr_1fr_1fr_0.8fr] gap-4 py-4 text-[10px] uppercase tracking-[0.2em] text-white/35">
              <span>Piece</span>
              <span className="text-right">Signature Revival</span>
              <span className="text-right">{first}'s Client Rate</span>
              <span className="text-right">They Save</span>
            </div>
            {d.examples.map((e, i) => (
              <div
                key={e.key}
                data-testid={`pricing-row-${e.key}`}
                className="grid grid-cols-2 sm:grid-cols-[1.4fr_1fr_1fr_0.8fr] gap-x-4 gap-y-1 py-6 border-t border-white/10 items-baseline fade-up"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <span className="font-display text-xl sm:text-2xl col-span-2 sm:col-span-1">{e.label}</span>
                <span className="text-white/35 text-base sm:text-right line-through decoration-[#c19a6b]/70" data-testid={`retail-${e.key}`}>
                  {money0(e.retail_cents)}
                </span>
                <span className="font-display text-2xl text-[#c19a6b] sm:text-right" data-testid={`client-rate-${e.key}`}>
                  {money0(e.client_cents)}
                </span>
                <span className="text-white/50 text-sm sm:text-right col-span-2 sm:col-span-1">{money0(e.savings_cents)} saved</span>
              </div>
            ))}
          </div>

          <div className="mt-14 grid sm:grid-cols-[1fr_auto] gap-8 items-center border border-[#c19a6b]/30 p-8">
            <p className="text-lg text-white/70 leading-relaxed">
              Your client receives the <strong className="text-white">Signature Revival</strong> experience while paying
              our <strong className="text-white">Essential Revival</strong> rate.
            </p>
            <div className="sm:text-right">
              <p className="text-[10px] uppercase tracking-[0.22em] text-white/40">Average saving per piece</p>
              <p className="font-display text-4xl text-[#c19a6b] mt-2" data-testid="average-savings">{money0(avgSavings)}</p>
            </div>
          </div>
        </div>
      </section>

      {/* REALTOR IS THE HERO */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <div className="max-w-2xl">
          <Kicker>Why this works for you</Kicker>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-5 leading-tight">
            Another reason your clients remember working with you.
          </h2>
          <p className="text-gray-600 mt-8 leading-relaxed text-[17px]">
            You've already helped them find or sell their home. Now you can help make the move itself a little easier.
          </p>
          <p className="text-gray-500 mt-5 leading-relaxed">
            As an ARK Partner, {r.display_name}'s clients receive access to pricing that isn't available to the general public.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-gray-200 mt-16">
          {[
            `No cost to ${first}`,
            "No contract",
            "No minimum referrals",
            `Nothing for ${first} to coordinate`,
            `Nothing for ${first} to sell`,
          ].map((t) => (
            <div key={t} className="bg-white px-6 py-10 text-center">
              <p className="font-display text-lg leading-snug">{t}</p>
            </div>
          ))}
        </div>
        <p className="font-display italic text-xl text-[#c19a6b] mt-12">ARK handles the entire customer experience.</p>
      </section>

      {/* THEIR CLIENT EXPERIENCE */}
      <section id="client-experience" className="bg-[#FAF7F2] py-24 sm:py-32 scroll-mt-14">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <Kicker>Their actual client experience</Kicker>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-5 leading-tight">
              Here's exactly what your clients will see.
            </h2>
            <p className="text-gray-500 mt-6 leading-relaxed">
              This is your live client page — personalized to you, ready to share today.
            </p>
          </div>

          <div className="mt-14 shadow-[0_30px_80px_rgba(0,0,0,0.12)]">
            <div className="bg-[#0B0B0B] px-4 py-3 flex items-center gap-3">
              <span className="flex gap-1.5">
                {["#c19a6b", "#ffffff40", "#ffffff40"].map((c, i) => (
                  <span key={i} className="w-2.5 h-2.5 rounded-full" style={{ background: c }} />
                ))}
              </span>
              <span className="text-[11px] text-white/50 truncate">{d.links.client_url.replace(/^https?:\/\//, "")}</span>
            </div>
            <div className="relative bg-white h-[420px] sm:h-[560px] overflow-hidden" data-testid="client-preview-frame">
              <iframe
                src={`/revival/${slug}?preview=1`}
                title="Client page preview"
                className="w-[1280px] h-[1500px] origin-top-left scale-[0.32] sm:scale-[0.55] pointer-events-none border-0"
              />
              <button
                data-testid="open-client-experience-btn"
                onClick={() => window.open(d.links.client_url, "_blank")}
                className="absolute inset-0 bg-transparent hover:bg-black/[0.03] transition-colors duration-200"
                aria-label="Open client page"
              />
            </div>
          </div>

          <div className="text-center mt-12">
            <a
              data-testid="experience-client-page-btn"
              href={d.links.client_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 h-14 px-10 bg-black text-white text-[11px] uppercase tracking-[0.22em] font-bold btn-lift"
            >
              Experience My Client Page <ArrowRight size={15} />
            </a>
          </div>
        </div>
      </section>

      {/* VALUE STACK */}
      <section className="bg-[#0B0B0B] text-white py-24 sm:py-32">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <Kicker>What you receive</Kicker>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-5 leading-tight max-w-2xl">
            As an ARK Realtor Partner, {first} receives:
          </h2>
          <div className="grid sm:grid-cols-2 gap-x-14 gap-y-1 mt-14">
            {valueStack.map((v, i) => (
              <div key={v} className="flex items-start gap-4 py-4 border-b border-white/10 fade-up" style={{ animationDelay: `${i * 40}ms` }}>
                <Check size={16} className="text-[#c19a6b] shrink-0 mt-1" />
                <span className="text-[15px] text-white/75 leading-snug">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ACTIVATION */}
      <section id="activate" className="bg-[#141414] text-white py-24 sm:py-32 scroll-mt-14">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          {!d.activated && (
            <div className="text-center max-w-2xl mx-auto mb-16">
              <Kicker>Activation takes a minute</Kicker>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-5 leading-tight">
                Activate My Complimentary Client Benefit
              </h2>
              <p className="text-white/50 mt-6 leading-relaxed">
                Confirm your details and we'll send your client materials right away.
              </p>
            </div>
          )}
          <ActivationPanel
            slug={slug}
            realtor={r}
            links={d.links}
            programName={d.program_name}
            clientMessage={d.client_message}
            activated={d.activated}
          />

          {d.booking_url && (
            <div className="text-center mt-16 pt-12 border-t border-white/10">
              <p className="text-white/45 text-sm mb-5">Prefer to talk it through first?</p>
              <a
                data-testid="book-call-btn"
                href={d.booking_url}
                target="_blank"
                rel="noreferrer"
                onClick={() => trackPartner(slug, "call_cta_click")}
                className="inline-flex items-center gap-2 h-12 px-8 border border-white/25 hover:border-[#c19a6b] text-white text-[11px] uppercase tracking-[0.2em] transition-colors duration-200"
              >
                <Phone size={14} /> Book a 10-Minute Call
              </a>
            </div>
          )}
        </div>
      </section>

      <footer className="bg-[#0B0B0B] text-white/40 py-14 text-center">
        <img src={LOGO_URL} alt="ARK Furniture" className="h-9 mx-auto mb-5 bg-white p-1.5" />
        <p className="text-[11px] uppercase tracking-[0.24em]">{d.program_name}</p>
        <p className="text-[11px] mt-3">Prepared for {r.display_name}{r.brokerage ? ` · ${r.brokerage}` : ""}</p>
        <div className="flex items-center justify-center gap-2 mt-6 text-white/25">
          <ArrowDown size={12} className="rotate-180" />
          <span className="text-[10px] tracking-[0.2em] uppercase">ARK Furniture Revival</span>
        </div>
      </footer>
    </div>
  );
}
